import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatSpread } from "@/lib/teams";
import { Radio } from "lucide-react";

const kickoffLabel = (iso) => {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (isNaN(d)) return "TBD";
  return d.toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
};

const periodLabel = (p) => (p <= 4 ? `Q${p}` : p === 5 ? "OT" : `${p - 4}OT`);

function GameStatus({ g }) {
  if (g.status === "final") {
    return <span className="text-[9px] uppercase tracking-wider text-white/40" data-testid="ticker-status-final">{g.period > 4 ? "Final/OT" : "Final"}</span>;
  }
  if (g.status === "in_progress") {
    const half = g.clock === "0:00" && g.period === 2;
    return (
      <span className="text-[10px] font-bold tabular-nums text-[#EAB308]" data-testid="ticker-status-live">
        {half ? "HALF" : `${periodLabel(g.period)} ${g.clock || ""}`}
      </span>
    );
  }
  return <span className="text-[10px] text-white/40 tabular-nums" data-testid="ticker-status-kickoff">{kickoffLabel(g.kickoff)}</span>;
}

function Team({ abbr, score, win, live, hasBall }) {
  return (
    <span className={`text-sm font-semibold inline-flex items-center gap-1 ${win ? "text-white" : live ? "text-white/90" : "text-white/60"}`}>
      {hasBall && <span className="w-1.5 h-1.5 rounded-full bg-[#EAB308]" title="Possession" />}
      {abbr}
      {score !== null && score !== undefined && <span className="tabular-nums">{score}</span>}
    </span>
  );
}

function GameCell({ g, showSpread }) {
  const final = g.status === "final";
  const live = g.status === "in_progress";
  const line = showSpread ? formatSpread(g) : null;
  return (
    <div className="inline-flex items-center gap-2 px-5 py-1.5 border-r border-white/10" data-testid={`ticker-game-${g.away}-${g.home}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${final ? "bg-[#22C55E]" : live ? "bg-[#EAB308] live-dot" : "bg-white/25"}`} />
      <Team abbr={g.away} score={g.away_score} win={final && g.winner === g.away} live={live} hasBall={live && g.possession === g.away} />
      <span className="text-white/30 text-xs">@</span>
      <Team abbr={g.home} score={g.home_score} win={final && g.winner === g.home} live={live} hasBall={live && g.possession === g.home} />
      <GameStatus g={g} />
      {line && <span className="text-[9px] font-bold text-[#EAB308]/80 tabular-nums" data-testid="ticker-spread">{line}</span>}
      <span className="text-[10px] text-[#E31837] font-bold">{g.points}</span>
    </div>
  );
}

export default function Ticker() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get("/scoreboard").then((res) => { if (alive) setData(res.data); }).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data || !data.games || data.games.length === 0) return null;

  const items = data.games;
  const showSpread = data.pick_mode === "ats" || data.pick_mode === "combo";
  const anyLive = items.some((g) => g.status === "in_progress");
  return (
    <div className="bg-[#141414] border-b border-white/10 overflow-hidden relative" data-testid="score-ticker">
      <div className="flex items-stretch">
        <div className="flex items-center gap-2 bg-[#E31837] px-4 shrink-0 z-10">
          <span className="font-['Bebas_Neue'] text-lg tracking-wide leading-none">WEEK {data.week_number}</span>
          {anyLive && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-black/30 px-1.5 py-0.5" data-testid="ticker-live-badge">
              <Radio className="w-3 h-3" /> Live
            </span>
          )}
        </div>
        <div className="overflow-hidden flex-1">
          <div className="ticker-track py-1.5">
            {[...items, ...items].map((g, i) => (
              <GameCell key={i} g={g} showSpread={showSpread} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
